import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { useBillingCycleSelect } from "@pallet/features/PartnershipTierType/BillingCycleSelector/hook"
import { Box, mq, Text, theme } from "@pallet-hq/hegel"
import { ReactNode } from "react"
import { VerticallyCenteredBox } from "components/atoms"
import { DiscountTag } from "./DiscountTag"
import { TierOverviewSectionFragment } from "__generated__/TierOverviewSectionFragment"

type PartnershipTierOverviewSectionProps = {
    partnershipTier: TierOverviewSectionFragment
    cta: ReactNode
    showOutline: boolean
}

export const PartnershipTierOverviewSection = ({
    partnershipTier,
    cta,
    showOutline,
}: PartnershipTierOverviewSectionProps) => {
    // @ts-ignore
    const { colors } = useTheme()
    const { billingCycle, getItem } = useBillingCycleSelect()
    const item = getItem(partnershipTier)

    return (
        <DivWrapper>
            <MainWrapper
                outlined={showOutline && partnershipTier.isHighlightedTier}
                color={colors.primary}
            >
                <VerticallyCenteredBox gap={theme.space.xsmall}>
                    <Text variant="headlineMedium">{partnershipTier.name}</Text>
                    {item?.discount && (
                        <DiscountTag discount={item?.discount} />
                    )}
                </VerticallyCenteredBox>
                <Description
                    variant="paragraphMedium"
                    mb="auto"
                    color="gray600"
                >
                    {partnershipTier.description}
                </Description>
                <VerticallyCenteredBox mt="small" gap={theme.space.xsmall}>
                    <Text variant="titleSmall">
                        {item?.formattedMonthlyPrice}
                    </Text>
                    <Text variant="uiSmall500" color="gray600">
                        per month
                    </Text>
                </VerticallyCenteredBox>
                {cta}
                {billingCycle && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        mt="xsmall"
                        width="100%"
                        height="16px"
                    >
                        {billingCycle != "Pay monthly" && (
                            <Text variant="uiSmall500" color="gray600">
                                {billingCycle === "Pay quarterly"
                                    ? "Billed quarterly"
                                    : "Billed annually"}
                            </Text>
                        )}
                    </Box>
                )}
            </MainWrapper>
        </DivWrapper>
    )
}

const DivWrapper = styled.div`
    display: flex;
    position: relative;
    box-sizing: border-box;
    ${mq({ gridRow: [undefined, 2] })}
`

const MainWrapper = styled.div<{ outlined: boolean; color: string }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${theme.space.small} ${theme.space.medium} ${theme.space.medium};
    background: ${theme.colors.white};
    border-radius: 8px 8px 0 0;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.12);
    border: 1px solid ${theme.colors.gray300};
    ${({ outlined, color }) =>
        outlined &&
        `
        border: 2px solid ${color};
        border-top: 0;
        border-radius: 0;
    `};
    border-bottom: 0;
`

const Description = styled(Text)`
    flex-grow: 1;
`
